// Cache Storage-backed download manager for offline learning.
// Stores resource bodies in the `makai-downloads-v1` Cache and tracks
// metadata in Dexie. Safe to call from any client component; no-ops on SSR.
import { offlineDB, type DownloadRecord } from "./db";

const CACHE_NAME = "makai-downloads-v1";

const SETTINGS_KEY = "makai.offline_settings.v1";
type Prefs = { wifiOnly: boolean; storageLimitMb: number };
function readPrefs(): Prefs {
  if (typeof window === "undefined") return { wifiOnly: false, storageLimitMb: 500 };
  try {
    const raw = localStorage.getItem(SETTINGS_KEY);
    const p = raw ? JSON.parse(raw) : {};
    return { wifiOnly: !!p.wifiOnly, storageLimitMb: Number(p.storageLimitMb) || 500 };
  } catch { return { wifiOnly: false, storageLimitMb: 500 }; }
}

function isCellular(): boolean {
  const c = (navigator as unknown as { connection?: { type?: string; effectiveType?: string } }).connection;
  if (!c) return false;
  return c.type === "cellular" || ["slow-2g", "2g", "3g"].includes(c.effectiveType ?? "");
}

async function totalDownloadedBytes(): Promise<number> {
  const db = offlineDB();
  if (!db) return 0;
  const all = await db.downloads.toArray();
  return all.reduce((s, r) => s + (r.size_bytes || 0), 0);
}

export type SaveOfflineInput = {
  id: string;
  kind: DownloadRecord["kind"];
  title: string;
  url: string;
  userId?: string;
  updatedAt?: string | null;
  onProgress?: (pct: number) => void;
};

export async function saveForOffline(input: SaveOfflineInput): Promise<DownloadRecord> {
  if (typeof window === "undefined" || !("caches" in window)) {
    throw new Error("Offline storage is not available in this browser.");
  }
  const db = offlineDB();
  if (!db) throw new Error("Offline database unavailable.");

  const prefs = readPrefs();
  if (prefs.wifiOnly && isCellular()) {
    throw new Error("On mobile data — enable downloads in Settings → Offline.");
  }

  const cache = await caches.open(CACHE_NAME);
  const cacheKey = `makai://download/${input.id}`;

  // Fetch with streaming progress.
  const res = await fetch(input.url, { credentials: "omit" });
  if (!res.ok) throw new Error(`Download failed (${res.status})`);
  const totalHeader = res.headers.get("content-length");
  const total = totalHeader ? Number(totalHeader) : 0;

  const chunks: Uint8Array[] = [];
  let received = 0;
  const reader = res.body?.getReader();
  if (reader) {
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      if (value) {
        chunks.push(value);
        received += value.byteLength;
        if (total && input.onProgress) input.onProgress(Math.min(100, Math.round((received / total) * 100)));
      }
    }
  } else {
    const buf = new Uint8Array(await res.arrayBuffer());
    chunks.push(buf);
    received = buf.byteLength;
  }

  // Storage cap check before commit.
  const currentTotal = await totalDownloadedBytes();
  const capBytes = prefs.storageLimitMb * 1024 * 1024;
  if (currentTotal + received > capBytes) {
    throw new Error(`Storage limit reached (${prefs.storageLimitMb} MB). Remove a download or raise the limit.`);
  }

  const blob = new Blob(chunks as BlobPart[], { type: res.headers.get("content-type") || "application/octet-stream" });
  await cache.put(cacheKey, new Response(blob, {
    headers: {
      "content-type": blob.type,
      "content-length": String(blob.size),
      "x-makai-title": encodeURIComponent(input.title),
    },
  }));

  const record: DownloadRecord = {
    id: input.id,
    kind: input.kind,
    title: input.title,
    url: input.url,
    cache_key: cacheKey,
    size_bytes: blob.size,
    downloaded_at: Date.now(),
    updated_at: input.updatedAt ?? null,
    user_id: input.userId,
  };
  await db.downloads.put(record);
  input.onProgress?.(100);
  return record;
}

export async function getOfflineRecord(id: string): Promise<DownloadRecord | null> {
  const db = offlineDB();
  if (!db) return null;
  return (await db.downloads.get(id)) ?? null;
}

export async function getOfflineBlobUrl(id: string): Promise<string | null> {
  if (typeof window === "undefined" || !("caches" in window)) return null;
  const rec = await getOfflineRecord(id);
  if (!rec) return null;
  const cache = await caches.open(CACHE_NAME);
  const res = await cache.match(rec.cache_key);
  if (!res) return null;
  const blob = await res.blob();
  return URL.createObjectURL(blob);
}

export async function removeDownload(id: string): Promise<void> {
  const db = offlineDB();
  if (!db) return;
  const rec = await db.downloads.get(id);
  if (rec && "caches" in window) {
    const cache = await caches.open(CACHE_NAME);
    await cache.delete(rec.cache_key);
  }
  await db.downloads.delete(id);
}

export async function isDownloaded(id: string): Promise<boolean> {
  const db = offlineDB();
  if (!db) return false;
  return !!(await db.downloads.get(id));
}

export async function listDownloads(): Promise<DownloadRecord[]> {
  const db = offlineDB();
  if (!db) return [];
  return db.downloads.orderBy("downloaded_at").reverse().toArray();
}
